// BLUE family is reserved for the future existing-restoration layer
// (composite, amalgam, crown). Exception: Cracked Tooth uses steel blue
// by founder decision because it is structural, not restorative.

import { SINGLE_PEN_STYLES } from "./singlePenStyles";

export const PULP_OUTLINE_COLOR = "#1a1a1a";

export const FINDING_PALETTE_COLORS = {
  caries: {
    label: "Caries",
    hueFamily: "red",
    baseColor: "#C8102E",
    shapeMarker: "solid",
    severitySteps: [
      { id: "caries_incipient", label: "Incipient", chartLabel: "Incipient", readbackLabel: "incipient caries", color: "#F7C8D0", isPulpInvolvement: false },
      { id: "caries_enamel", label: "Enamel", chartLabel: "Enamel", readbackLabel: "enamel caries", color: "#EF7C8E", isPulpInvolvement: false },
      { id: "caries_dentin", label: "Dentin", chartLabel: "Dentin", readbackLabel: "dentin caries", color: "#E84B5C", isPulpInvolvement: false },
      { id: "caries_deep_dentin", label: "Deep Dentin", chartLabel: "Deep Dentin", readbackLabel: "near-pulp caries", color: "#A01525", isPulpInvolvement: false },
      { id: "caries_pulpal", label: "Pulpal", chartLabel: "Pulpal", readbackLabel: "pulp involvement", color: "#4A0810", isPulpInvolvement: true },
    ],
  },
  defective_restoration: {
    label: "Defective Restoration",
    hueFamily: "orange",
    baseColor: "#E86F1D",
    shapeMarker: "solid",
    severitySteps: [
      { id: "defective_restoration_marginal", label: "Marginal", chartLabel: "Marginal", readbackLabel: "marginal breakdown", color: "#F8D2B8", isPulpInvolvement: false },
      { id: "defective_restoration_open", label: "Open", chartLabel: "Open", readbackLabel: "open margin", color: "#D96A1B", isPulpInvolvement: false },
      { id: "defective_restoration_lost", label: "Lost", chartLabel: "Lost", readbackLabel: "restoration lost", color: "#7A2F0B", isPulpInvolvement: false },
    ],
  },
  fracture: {
    label: "Fracture",
    hueFamily: "purple",
    baseColor: "#6D28D9",
    shapeMarker: "slash",
    severitySteps: [
      { id: "fracture_enamel_craze", label: "Enamel craze", chartLabel: "Enamel craze", readbackLabel: "hairline crack", color: "#E9D5FF", isPulpInvolvement: false },
      { id: "fracture_enamel_chip", label: "Enamel chip", chartLabel: "Enamel chip", readbackLabel: "enamel chip", color: "#C084FC", isPulpInvolvement: false },
      { id: "fracture_dentin", label: "Into dentin", chartLabel: "Into dentin", readbackLabel: "into dentin", color: "#A855F7", isPulpInvolvement: false },
      { id: "fracture_cusp", label: "Cusp fracture", chartLabel: "Cusp fracture", readbackLabel: "cusp fracture", color: "#5B21B6", isPulpInvolvement: false },
      { id: "fracture_pulpal", label: "Pulpal", chartLabel: "Pulpal", readbackLabel: "pulp involvement", color: "#2E1065", isPulpInvolvement: true },
    ],
  },
  wear: {
    label: "Attrition",
    displayLabel: "Attrition (Wear)",
    readbackLabel: "Wear (attrition)",
    hueFamily: "brown",
    baseColor: "#A16207",
    shapeMarker: "hatched",
    severitySteps: [
      { id: "wear_enamel", label: "Enamel", chartLabel: "Enamel", readbackLabel: "enamel wear", color: "#F3E2C2", isPulpInvolvement: false },
      { id: "wear_dentin", label: "Dentin", chartLabel: "Dentin", readbackLabel: "dentin wear", color: "#B7791F", isPulpInvolvement: false },
      { id: "wear_severe_dentin", label: "Severe Dentin", chartLabel: "Severe Dentin", readbackLabel: "severe wear", color: "#7C4A10", isPulpInvolvement: false },
      { id: "wear_pulpal", label: "Pulpal", chartLabel: "Pulpal", readbackLabel: "pulp involvement", color: "#4A2A0A", isPulpInvolvement: true },
    ],
  },
  mobility: {
    label: "Mobility",
    hueFamily: "violet",
    baseColor: "#4F46E5",
    shapeMarker: "ring",
    severitySteps: [
      { id: "mobility_g1", label: "Grade 1", chartLabel: "Grade 1", readbackLabel: "Grade 1 mobility", color: "#E0E7FF", isPulpInvolvement: false },
      { id: "mobility_g2", label: "Grade 2", chartLabel: "Grade 2", readbackLabel: "Grade 2 mobility", color: "#6366F1", isPulpInvolvement: false },
      { id: "mobility_g3", label: "Grade 3", chartLabel: "Grade 3", readbackLabel: "Grade 3 mobility", color: "#312E81", isPulpInvolvement: false },
    ],
  },
  perio_pocket: {
    label: "Pocket",
    hueFamily: "magenta",
    baseColor: "#DB2777",
    shapeMarker: "ring",
    severitySteps: [
      { id: "perio_pocket_4_5", label: "4–5mm", chartLabel: "4–5mm", readbackLabel: "4–5mm pocket", color: "#FBCFE8", isPulpInvolvement: false },
      { id: "perio_pocket_6_7", label: "6–7mm", chartLabel: "6–7mm", readbackLabel: "6–7mm pocket", color: "#DB2777", isPulpInvolvement: false },
      { id: "perio_pocket_8_plus", label: "8mm+", chartLabel: "8mm+", readbackLabel: "8mm+ pocket", color: "#831843", isPulpInvolvement: false },
    ],
  },
  calculus: {
    label: "Calculus",
    hueFamily: "teal",
    baseColor: "#0D9488",
    shapeMarker: "solid",
    severitySteps: [
      { id: "calculus_supra", label: "Supragingival", chartLabel: "Supragingival", readbackLabel: "supragingival calculus", color: "#CCFBF1", isPulpInvolvement: false },
      { id: "calculus_sub", label: "Subgingival", chartLabel: "Subgingival", readbackLabel: "subgingival calculus", color: "#0F766E", isPulpInvolvement: false },
    ],
  },
  recession: {
    label: "Recession",
    hueFamily: "amber",
    baseColor: "#F59E0B",
    shapeMarker: "slash",
    severitySteps: [
      { id: "recession_1_2mm", label: "1–2mm", chartLabel: "1–2mm", readbackLabel: "1–2mm recession", color: "#FEF3C7", isPulpInvolvement: false },
      { id: "recession_3_4mm", label: "3–4mm", chartLabel: "3–4mm", readbackLabel: "3–4mm recession", color: "#F59E0B", isPulpInvolvement: false },
      { id: "recession_5mm_plus", label: "5mm+", chartLabel: "5mm+", readbackLabel: "5mm+ recession", color: "#92400E", isPulpInvolvement: false },
    ],
  },
  pulpitis: {
    label: "Pulpitis",
    hueFamily: "crimson",
    baseColor: "#B91C1C",
    shapeMarker: "ring",
    severitySteps: [
      { id: "pulpitis_reversible", label: "Reversible", chartLabel: "Reversible", readbackLabel: "reversible pulpitis", color: "#FECACA", isPulpInvolvement: false },
      { id: "pulpitis_irreversible", label: "Irreversible", chartLabel: "Irreversible", readbackLabel: "irreversible pulpitis", color: "#7F1D1D", isPulpInvolvement: false },
    ],
  },
  inflammation: {
    label: "Inflammation",
    hueFamily: "salmon",
    baseColor: "#F97366",
    shapeMarker: "solid",
    severitySteps: [
      { id: "inflammation_mild_gi1", label: "Mild (GI 1)", chartLabel: "Mild (GI 1)", readbackLabel: "mild gingival inflammation", color: "#FFE2DD", isPulpInvolvement: false },
      { id: "inflammation_moderate_gi2", label: "Moderate (GI 2)", chartLabel: "Moderate (GI 2)", readbackLabel: "moderate gingival inflammation", color: "#F97366", isPulpInvolvement: false },
      { id: "inflammation_severe_gi3", label: "Severe (GI 3)", chartLabel: "Severe (GI 3)", readbackLabel: "severe gingival inflammation", color: "#9F1239", isPulpInvolvement: false },
    ],
  },
  necrotic_pulp: { label: "Necrotic Pulp", readbackLabel: "necrotic pulp", hueFamily: "near-black maroon", baseColor: "#2A0708", shapeMarker: "ring" },
  periapical_lesion: {
    label: "Periapical",
    hueFamily: "burgundy",
    baseColor: "#5F0F25",
    shapeMarker: "ring",
    severitySteps: [
      { id: "periapical_acute", label: "Acute", chartLabel: "Acute", readbackLabel: "acute periapical", color: "#F8C7D0", isPulpInvolvement: false },
      { id: "periapical_chronic", label: "Chronic", chartLabel: "Chronic", readbackLabel: "chronic periapical", color: "#9F1239", isPulpInvolvement: false },
      { id: "periapical_suppurative", label: "Suppurative", chartLabel: "Suppurative", readbackLabel: "draining periapical", color: "#5F0F25", isPulpInvolvement: false },
    ],
  },
  failed_rct: { label: "Failed RCT", readbackLabel: "failed RCT", hueFamily: "dark teal slate", baseColor: "#164E63", shapeMarker: "slash" },
  furcation: {
    label: "Furcation",
    hueFamily: "lavender",
    baseColor: "#A78BFA",
    shapeMarker: "ring",
    severitySteps: [
      { id: "furcation_class_i", label: "Class I", chartLabel: "Class I", readbackLabel: "Class I furcation", color: "#EDE9FE", isPulpInvolvement: false },
      { id: "furcation_class_ii", label: "Class II", chartLabel: "Class II", readbackLabel: "Class II furcation", color: "#A78BFA", isPulpInvolvement: false },
      { id: "furcation_class_iii", label: "Class III", chartLabel: "Class III", readbackLabel: "Class III furcation", color: "#5B21B6", isPulpInvolvement: false },
    ],
  },
  impacted: { label: "Impacted", readbackLabel: "impacted", hueFamily: "gray", baseColor: "#6B7280", shapeMarker: "solid" },
  abrasion: {
    label: "Abrasion",
    hueFamily: "peach",
    baseColor: "#FDBA74",
    shapeMarker: "hatched",
    severitySteps: [
      { id: "abrasion_mild", label: "Mild", chartLabel: "Mild", readbackLabel: "mild abrasion", color: "#FFE4CC", isPulpInvolvement: false },
      { id: "abrasion_moderate", label: "Moderate", chartLabel: "Moderate", readbackLabel: "moderate abrasion", color: "#FDBA74", isPulpInvolvement: false },
      { id: "abrasion_severe", label: "Severe", chartLabel: "Severe", readbackLabel: "severe abrasion", color: "#9A4D12", isPulpInvolvement: false },
    ],
  },
  abfraction: {
    label: "Abfraction",
    hueFamily: "dusty rose",
    baseColor: "#BE6F7A",
    shapeMarker: "hatched",
    severitySteps: [
      { id: "abfraction_mild", label: "Mild", chartLabel: "Mild", readbackLabel: "mild abfraction", color: "#F3D2D8", isPulpInvolvement: false },
      { id: "abfraction_moderate", label: "Moderate", chartLabel: "Moderate", readbackLabel: "moderate abfraction", color: "#BE6F7A", isPulpInvolvement: false },
      { id: "abfraction_severe", label: "Severe", chartLabel: "Severe", readbackLabel: "severe abfraction", color: "#7F303D", isPulpInvolvement: false },
    ],
  },
  erosion: {
    label: "Erosion",
    hueFamily: "sand beige",
    baseColor: "#D6B887",
    shapeMarker: "hatched",
    severitySteps: [
      { id: "erosion_enamel", label: "Enamel", chartLabel: "Enamel", readbackLabel: "enamel erosion", color: "#F3EAD8", isPulpInvolvement: false },
      { id: "erosion_dentin", label: "Dentin", chartLabel: "Dentin", readbackLabel: "dentin erosion", color: "#D6B887", isPulpInvolvement: false },
      { id: "erosion_pulpal", label: "Pulpal", chartLabel: "Pulpal", readbackLabel: "pulp involvement", color: "#755936", isPulpInvolvement: true },
    ],
  },
  bleeding_on_probing: { label: "Bleeding", readbackLabel: "bleeding on probing", hueFamily: "bright red-pink", baseColor: "#E11D48", shapeMarker: "solid" },
  cracked_tooth: {
    label: "Cracked Tooth",
    hueFamily: "steel blue",
    baseColor: "#4682B4",
    shapeMarker: "slash",
    severitySteps: [
      { id: "cracked_tooth_craze_line", label: "Craze line", chartLabel: "Craze line", readbackLabel: "craze line", color: "#DBEAFE", isPulpInvolvement: false },
      { id: "cracked_tooth_fractured_cusp", label: "Fractured cusp", chartLabel: "Fractured cusp", readbackLabel: "fractured cusp", color: "#60A5FA", isPulpInvolvement: false },
      { id: "cracked_tooth_cracked", label: "Cracked tooth", chartLabel: "Cracked tooth", readbackLabel: "cracked tooth", color: "#2563EB", isPulpInvolvement: false },
      { id: "cracked_tooth_split", label: "Split tooth", chartLabel: "Split tooth", readbackLabel: "split tooth", color: "#1E3A8A", isPulpInvolvement: false },
    ],
  },
  open_contact: { label: "Open Contact", readbackLabel: "open contact", hueFamily: "light gray-green", baseColor: "#8AA99A", shapeMarker: "solid" },

  rct_needed: { label: "RCT Needed", hueFamily: "orange", baseColor: SINGLE_PEN_STYLES.rct_needed.fillColor, shapeMarker: "ring" },
  missing: { label: "Missing", hueFamily: "gray", baseColor: SINGLE_PEN_STYLES.missing.fillColor, shapeMarker: "slash" },
  to_extract: { label: "To Extract", hueFamily: "red-pink", baseColor: SINGLE_PEN_STYLES.to_extract.fillColor, shapeMarker: "slash" },
  sensitivity: { label: "Sensitivity", hueFamily: "light yellow", baseColor: SINGLE_PEN_STYLES.sensitivity.fillColor, shapeMarker: "solid" },
  discoloration: { label: "Discoloration", hueFamily: "taupe", baseColor: SINGLE_PEN_STYLES.discoloration.fillColor, shapeMarker: "solid" },
  stain_discoloration: { label: "Discoloration / Stain", hueFamily: "taupe", baseColor: SINGLE_PEN_STYLES.discoloration.fillColor, shapeMarker: "solid" },
  watch: { label: "Watch", hueFamily: "blue-gray", baseColor: SINGLE_PEN_STYLES.watch.fillColor, shapeMarker: "solid" },
};

export function applyFindingPaletteColor(rawCategory = {}) {
  const code = rawCategory.code;
  const config = FINDING_PALETTE_COLORS[code];
  if (!config) return rawCategory;

  const baseColor = config.baseColor;
  const severityLevels = (rawCategory.severityLevels || rawCategory.severity_levels || []).map((rawLevel) => {
    const step = config.severitySteps?.find((item) => item.id === rawLevel.code || item.id === rawLevel.id);
    const color = step?.color || rawLevel.colorHex || rawLevel.color_hex || baseColor;
    const chartLabel = step?.chartLabel || rawLevel.chartLabel || rawLevel.chart_label || step?.label || rawLevel.label;
    const readbackLabel = step?.readbackLabel || rawLevel.readbackLabel || rawLevel.readback_label || rawLevel.patientLabel || rawLevel.patient_label || chartLabel;
    return {
      ...rawLevel,
      label: chartLabel,
      chartLabel,
      chart_label: chartLabel,
      readbackLabel,
      readback_label: readbackLabel,
      colorHex: color,
      color_hex: color,
      textColorHex: rawLevel.textColorHex || rawLevel.text_color_hex || readableTextColor(color),
      text_color_hex: rawLevel.text_color_hex || rawLevel.textColorHex || readableTextColor(color),
      isPulpInvolvement: step?.isPulpInvolvement ?? rawLevel.isPulpInvolvement ?? rawLevel.is_pulp_involvement ?? false,
      is_pulp_involvement: step?.isPulpInvolvement ?? rawLevel.is_pulp_involvement ?? rawLevel.isPulpInvolvement ?? false,
    };
  });

  return {
    ...rawCategory,
    singlePenStyle: SINGLE_PEN_STYLES[code] || null,
    single_pen_style: SINGLE_PEN_STYLES[code] || null,
    label: config.label || rawCategory.chartLabel || rawCategory.chart_label || rawCategory.label,
    displayLabel: config.displayLabel || rawCategory.displayLabel || rawCategory.display_label || rawCategory.label,
    chartLabel: config.label || rawCategory.chartLabel || rawCategory.chart_label || rawCategory.label,
    chart_label: config.label || rawCategory.chart_label || rawCategory.chartLabel || rawCategory.label,
    readbackLabel: config.readbackLabel || rawCategory.readbackLabel || rawCategory.readback_label || rawCategory.patientLabel || rawCategory.patient_label,
    readback_label: config.readbackLabel || rawCategory.readback_label || rawCategory.readbackLabel || rawCategory.patient_label || rawCategory.patientLabel,
    hueFamily: config.hueFamily,
    hue_family: config.hueFamily,
    shapeMarker: config.shapeMarker,
    shape_marker: config.shapeMarker,
    colorHex: baseColor,
    color_hex: baseColor,
    textColorHex: rawCategory.textColorHex || rawCategory.text_color_hex || readableTextColor(baseColor),
    text_color_hex: rawCategory.text_color_hex || rawCategory.textColorHex || readableTextColor(baseColor),
    severityLevels,
    severity_levels: severityLevels,
  };
}

export function readableTextColor(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return "#0f172a";
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq >= 150 ? "#0f172a" : "#ffffff";
}

function parseHex(hex) {
  const clean = String(hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}
